/**
 * Rubros de negocio de Aturno.
 *
 * Fuente única de verdad, compartida por el onboarding y la landing:
 *  - El onboarding la usa para el selector de tipo de negocio, los textos de
 *    cada paso y los servicios/recursos/roles sugeridos.
 *  - La landing la usa para la sección de rubros y los emojis del hero.
 *
 * Estaba embebida en Onboarding.jsx. Al vivir acá, agregar un rubro nuevo lo
 * hace aparecer en el producto y en el marketing a la vez, sin desincronizarse.
 *
 * Sobre las sugerencias (`suggested*`): son solo valores iniciales para que el
 * negocio no arranque de una pantalla vacía. El onboarding las carga y a partir
 * de ahí se editan como cualquier otro dato, acá y después en el dashboard.
 *  - `suggestedServices`: { name, duration (minutos), price (ARS) }
 *  - `suggestedResources`: { name, description } — espacios/equipos reservables.
 *    Nada que no se pueda reservar (sala de espera, vestuarios) va acá.
 *  - `suggestedStaffRoles`: roles del rubro, no nombres de personas. Se ofrecen
 *    como atajo para abrir el formulario de personal con el puesto ya escrito.
 *
 * Sobre `usaEspaciosReservables`: decide si el onboarding muestra el paso de
 * espacios. Está en true solo donde el lugar ES lo que se reserva —una cancha,
 * una sala, un aula—, porque ahí puede no haber personal y el lugar es lo único
 * que limita cuántos turnos entran a la misma hora.
 *
 * En una peluquería o un consultorio pasa al revés: lo que limita es la persona,
 * que se carga en el paso anterior, y un recurso solo hace algo si además se lo
 * asigna a un servicio. Cargar sillones ahí era pedir un dato que no cambiaba
 * nada. `unidadReservable` se mantiene igual en los siete porque el paso también
 * se puede activar después desde el panel.
 */

export const businessTemplates = [
    {
        id: 'beauty',
        name: 'Belleza y Estética',
        description: 'Peluquería, barbería, salón de belleza, spa',
        icon: '💇‍♀️',
        template: {
            businessNamePlaceholder: 'Ej: Salón María Estética',
            descriptionPlaceholder: 'Describe tu salón de belleza, servicios principales y lo que te hace especial...',
            addressPlaceholder: 'Av. Corrientes 1234, CABA',
            servicesTitle: 'Servicios de belleza',
            servicesDescription: 'Agregá tus tratamientos, cortes, peinados y servicios estéticos. Cada servicio puede tener su duración y precio.',
            suggestedServices: [
                { name: 'Corte de pelo', duration: 45, price: 3500 },
                { name: 'Peinado', duration: 60, price: 4000 },
                { name: 'Coloración', duration: 120, price: 8000 }
            ],
            usaEspaciosReservables: false,
            unidadReservable: { singular: 'sillón', plural: 'sillones', genero: 'm', nombre: 'Sillón' },
            resourcesTitle: 'Espacios y equipamiento',
            resourcesDescription: 'Agregá tus sillones, cabinas, salas de tratamiento y equipos disponibles.',
            suggestedResources: [
                { name: 'Sillón 1', description: 'Sillón de corte y peinado' },
                { name: 'Sillón 2', description: 'Sillón de corte y peinado' },
                { name: 'Cabina privada', description: 'Cabina para tratamientos de estética' },
                { name: 'Sala de coloración', description: 'Espacio para tinturas y mechas' }
            ],
            staffTitle: 'Equipo de profesionales',
            staffDescription: 'Agregá a tu equipo de estilistas, peluqueros y especialistas en belleza.',
            suggestedStaffRoles: ['Estilista', 'Colorista', 'Barbero', 'Manicura']
        }
    },
    {
        id: 'health',
        name: 'Salud y Bienestar',
        description: 'Consultorio médico, psicología, nutrición',
        icon: '🏥',
        template: {
            businessNamePlaceholder: 'Ej: Consultorio Dr. Pérez',
            descriptionPlaceholder: 'Describe tu consultorio, especialidades médicas y servicios que ofreces...',
            addressPlaceholder: 'Av. Santa Fe 2500, CABA',
            servicesTitle: 'Consultas y tratamientos',
            servicesDescription: 'Agregá tus consultas médicas, terapias y tratamientos. Define la duración de cada consulta.',
            suggestedServices: [
                { name: 'Consulta general', duration: 30, price: 5000 },
                { name: 'Consulta especializada', duration: 45, price: 7500 },
                { name: 'Seguimiento', duration: 20, price: 3500 }
            ],
            usaEspaciosReservables: false,
            unidadReservable: { singular: 'consultorio', plural: 'consultorios', genero: 'm', nombre: 'Consultorio' },
            resourcesTitle: 'Consultorios y equipamiento',
            resourcesDescription: 'Agregá tus consultorios y las salas donde atendés. Cada uno se puede asignar a un servicio.',
            suggestedResources: [
                { name: 'Consultorio 1', description: 'Consultorio de atención general' },
                { name: 'Consultorio 2', description: 'Consultorio de atención general' },
                { name: 'Sala de procedimientos', description: 'Sala equipada para prácticas' }
            ],
            staffTitle: 'Equipo médico',
            staffDescription: 'Agregá a tu equipo de médicos, especialistas y personal de salud.',
            suggestedStaffRoles: ['Médico clínico', 'Psicólogo', 'Nutricionista', 'Kinesiólogo']
        }
    },
    {
        id: 'fitness',
        name: 'Fitness y Deporte',
        description: 'Gimnasio, entrenamiento personal, clases grupales',
        icon: '💪',
        template: {
            businessNamePlaceholder: 'Ej: Gimnasio FitZone',
            descriptionPlaceholder: 'Describe tu gimnasio, clases que ofreces y servicios de entrenamiento...',
            addressPlaceholder: 'Av. Cabildo 1500, CABA',
            servicesTitle: 'Clases y entrenamientos',
            servicesDescription: 'Agregá tus clases grupales, entrenamientos personales y servicios fitness.',
            suggestedServices: [
                { name: 'Entrenamiento personal', duration: 60, price: 4000 },
                { name: 'Clase grupal', duration: 45, price: 1500 },
                { name: 'Evaluación física', duration: 30, price: 2500 }
            ],
            usaEspaciosReservables: true,
            unidadReservable: { singular: 'sala', plural: 'salas', genero: 'f', nombre: 'Sala' },
            resourcesTitle: 'Espacios y equipamiento',
            resourcesDescription: 'Agregá tus salas, máquinas y espacios disponibles para entrenamientos.',
            suggestedResources: [
                { name: 'Sala de musculación', description: 'Máquinas y peso libre' },
                { name: 'Sala de cardio', description: 'Cintas, bicicletas y elípticos' },
                { name: 'Sala de clases', description: 'Espacio para clases grupales' },
                { name: 'Área funcional', description: 'Espacio para entrenamiento funcional' }
            ],
            staffTitle: 'Equipo de entrenadores',
            staffDescription: 'Agregá a tu equipo de entrenadores personales, instructores y profesores.',
            suggestedStaffRoles: ['Entrenador personal', 'Instructor de clases grupales', 'Profesor de yoga']
        }
    },
    {
        id: 'sports',
        name: 'Deportes y Canchas',
        description: 'Fútbol, pádel, básquet, tenis, canchas deportivas',
        icon: '⚽',
        template: {
            businessNamePlaceholder: 'Ej: Club Deportivo Los Pinos',
            descriptionPlaceholder: 'Describe tu club, canchas disponibles y deportes que se practican...',
            addressPlaceholder: 'Av. del Libertador 3000, CABA',
            servicesTitle: 'Alquiler de canchas y clases',
            servicesDescription: 'Agregá el alquiler de tus canchas por hora y las clases deportivas que ofreces.',
            suggestedServices: [
                { name: 'Cancha de fútbol 5', duration: 60, price: 8000 },
                { name: 'Cancha de pádel', duration: 90, price: 6000 },
                { name: 'Clase de tenis', duration: 60, price: 4500 }
            ],
            usaEspaciosReservables: true,
            unidadReservable: { singular: 'cancha', plural: 'canchas', genero: 'f', nombre: 'Cancha' },
            resourcesTitle: 'Canchas y espacios deportivos',
            resourcesDescription: 'Agregá todas tus canchas. Si tenés tres canchas iguales, creá tres recursos: así se pueden reservar en paralelo.',
            suggestedResources: [
                { name: 'Cancha fútbol 5 - A', description: 'Césped sintético, con luces' },
                { name: 'Cancha fútbol 5 - B', description: 'Césped sintético, con luces' },
                { name: 'Cancha pádel 1', description: 'Cancha de pádel con paredes de blindex' },
                { name: 'Cancha pádel 2', description: 'Cancha de pádel con paredes de blindex' },
                { name: 'Cancha de tenis', description: 'Cancha de polvo de ladrillo' }
            ],
            staffTitle: 'Equipo de profesores y encargados',
            staffDescription: 'Agregá a tu equipo de profesores deportivos, encargados de canchas y personal.',
            suggestedStaffRoles: ['Profesor de tenis', 'Profesor de pádel', 'Encargado de canchas']
        }
    },
    {
        id: 'education',
        name: 'Educación y Capacitación',
        description: 'Clases particulares, cursos, talleres',
        icon: '📚',
        template: {
            businessNamePlaceholder: 'Ej: Academia de Idiomas',
            descriptionPlaceholder: 'Describe tu centro educativo, materias que enseñas y metodología...',
            addressPlaceholder: 'Av. Rivadavia 1200, CABA',
            servicesTitle: 'Clases y cursos',
            servicesDescription: 'Agregá tus clases particulares, cursos grupales y talleres educativos.',
            suggestedServices: [
                { name: 'Clase particular', duration: 60, price: 3000 },
                { name: 'Clase grupal', duration: 90, price: 2000 },
                { name: 'Taller intensivo', duration: 180, price: 8000 }
            ],
            usaEspaciosReservables: true,
            unidadReservable: { singular: 'aula', plural: 'aulas', genero: 'f', nombre: 'Aula' },
            resourcesTitle: 'Aulas y espacios educativos',
            resourcesDescription: 'Agregá tus aulas, laboratorios y espacios disponibles para clases.',
            suggestedResources: [
                { name: 'Aula 1', description: 'Aula para clases grupales' },
                { name: 'Aula 2', description: 'Aula para clases grupales' },
                { name: 'Aula individual', description: 'Espacio para clases particulares' },
                { name: 'Laboratorio', description: 'Aula equipada para prácticas' },
                { name: 'Sala de conferencias', description: 'Espacio para talleres y charlas' }
            ],
            staffTitle: 'Equipo docente',
            staffDescription: 'Agregá a tu equipo de profesores, tutores y personal educativo.',
            suggestedStaffRoles: ['Profesor de inglés', 'Profesor de matemática', 'Tutor']
        }
    },
    {
        id: 'services',
        name: 'Servicios Profesionales',
        description: 'Consultoría, asesoría, servicios técnicos',
        icon: '💼',
        template: {
            businessNamePlaceholder: 'Ej: Consultora Estratégica',
            descriptionPlaceholder: 'Describe tus servicios profesionales, especialidades y experiencia...',
            addressPlaceholder: 'Av. 9 de Julio 1000, CABA',
            servicesTitle: 'Servicios profesionales',
            servicesDescription: 'Agregá tus servicios de consultoría, asesorías y servicios técnicos especializados.',
            suggestedServices: [
                { name: 'Consultoría estratégica', duration: 120, price: 12000 },
                { name: 'Asesoría legal', duration: 60, price: 8000 },
                { name: 'Auditoría', duration: 240, price: 20000 }
            ],
            usaEspaciosReservables: false,
            unidadReservable: { singular: 'oficina', plural: 'oficinas', genero: 'f', nombre: 'Oficina' },
            resourcesTitle: 'Oficinas y espacios de trabajo',
            resourcesDescription: 'Agregá tus oficinas, salas de reuniones y espacios disponibles para consultas.',
            suggestedResources: [
                { name: 'Oficina principal', description: 'Oficina de atención al cliente' },
                { name: 'Sala de reuniones', description: 'Sala para reuniones de equipo' },
                { name: 'Oficina privada', description: 'Espacio para consultas confidenciales' }
            ],
            staffTitle: 'Equipo profesional',
            staffDescription: 'Agregá a tu equipo de consultores, asesores y profesionales especializados.',
            suggestedStaffRoles: ['Consultor', 'Abogado', 'Contador']
        }
    },
    {
        id: 'other',
        name: 'Otro',
        description: 'Otros tipos de negocio',
        icon: '🏢',
        template: {
            businessNamePlaceholder: 'Ej: Mi Negocio',
            descriptionPlaceholder: 'Describe tu negocio, servicios que ofreces y lo que te hace especial...',
            addressPlaceholder: 'Tu dirección comercial',
            servicesTitle: 'Servicios principales',
            servicesDescription: 'Agregá los servicios que ofreces con su duración y precio.',
            suggestedServices: [
                { name: 'Servicio principal', duration: 60, price: 5000 },
                { name: 'Consulta inicial', duration: 30, price: 3000 }
            ],
            usaEspaciosReservables: false,
            unidadReservable: { singular: 'espacio', plural: 'espacios', genero: 'm', nombre: 'Espacio' },
            resourcesTitle: 'Recursos y espacios',
            resourcesDescription: 'Agregá los espacios, equipos o recursos que usás para dar tus servicios.',
            suggestedResources: [
                { name: 'Espacio principal', description: 'Área de trabajo principal' },
                { name: 'Espacio secundario', description: 'Segundo espacio de atención' }
            ],
            staffTitle: 'Equipo de trabajo',
            staffDescription: 'Agregá a tu equipo de trabajo y colaboradores.',
            suggestedStaffRoles: ['Responsable', 'Asistente']
        }
    }
];


/**
 * Vista reducida para la landing: solo lo que se muestra en las tarjetas.
 * 'other' queda afuera a propósito — es el comodín del onboarding, no un rubro
 * que tenga sentido publicitar.
 */
export const rubros = businessTemplates
  .filter((t) => t.id !== 'other')
  .map(({ id, name, description, icon }) => ({ id, name, description, icon }));

/** Los emojis, para la decoración del hero. */
export const rubroEmojis = rubros.map((r) => r.icon);
